import { useState } from 'react';
import { Button, message, Table, Alert, Input } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import {
    ContentCard,
    useUsers,
    getUserColumnsWithActions,
    type UserData,
} from '../../component/admin';
import {
    UserEditModal,
    UserRegisterModal,
    UserLogModal,
} from '../../component/admin';
import {
    editUserName,
    editUserPassword,
    withdrawUser,
    viewPostLogs,
    changeUserRole,
} from '../../api/adminAPI';

const UserManagePage = () => {
    const { users, loading, error, refetch, setSearchKeyword } = useUsers();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
    const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
    const [isLogModalOpen, setIsLogModalOpen] = useState(false);
    const [logTargetUser, setLogTargetUser] = useState<UserData | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);

    const handleViewLogs = (user: UserData) => {
        setLogTargetUser(user);
        setIsLogModalOpen(true);
    };

    const handleEdit = (user: UserData) => {
        setSelectedUser(user);
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedUser(null);
    };

    const handleModalSubmit = async (data: any) => {
        try {
            const [, roleResult] = await Promise.all([
                editUserName(data.userId, data.name),
                changeUserRole({
                    userId: data.userId,
                    englishName: data.englishName,
                    role: data.role,
                    email: data.email,
                    birthDate: data.birthDate,
                    phone: data.phone,
                    workplace: data.workplace,
                    medicalLicenseNumber: data.medicalLicenseNumber,
                }),
            ]);

            if (!roleResult.success) {
                message.error(roleResult.message);
                return;
            }

            message.success('유저 정보가 성공적으로 변경되었습니다.');
            refetch();
            handleModalClose();
        } catch (e) {
            console.log(e);
            message.error('유저 정보 변경에 실패했습니다.');
        }
    };

    const handlePasswordChange = (userId: number, newPassword: string) => {
        editUserPassword(userId, newPassword)
            .then(() => message.success('비밀번호가 성공적으로 변경되었습니다.'))
            .catch((e) => {
                console.log(e);
                message.error('비밀번호 변경에 실패했습니다.');
            });
    };

    const handleDeleteUser = async (userId: number) => {
        try {
            await withdrawUser(userId);
            message.success('유저가 성공적으로 탈퇴 처리되었습니다.');
            refetch();
        } catch (e) {
            message.error('유저 탈퇴 처리에 실패했습니다.');
        }
    };

    const columns = getUserColumnsWithActions({
        onEdit: handleEdit,
        onViewLogs: handleViewLogs,
    });

    const clickHiddenBtn = () => {
        viewPostLogs().then(data => console.log(data));
    };

    if (loading) return <div>loading...</div>;
    if (error) return <div>error: {error}</div>;

    const activeUsers = users?.filter(user => !user.isWithdrawnYN) || []; // 탈퇴하지 않은 유저만 표시

    return (
        <>
            <ContentCard title="유저 관리">
                <AdminInfoBox>
                    <Alert
                        message="사용자 Role 안내"
                        description={
                            <ul style={{ margin: 0, paddingLeft: '20px', marginTop: '8px' }}>
                                <li style={{ marginBottom: '4px' }}><strong>관리자:</strong> 모든 권한 (PATHOLOGY INFORMATION 관리자만 보이게 &rarr; 병리번호 적혀있음)</li>
                                <li style={{ marginBottom: '4px' }}><strong>자문위원:</strong> 회원공간 이용 가능 / annotation 가능 / 게시글 수정 가능</li>
                                <li style={{ marginBottom: '4px' }}><strong>특별회원:</strong> 정회원 중 관리자 승인 / 회원공간 이용 불가 / annotation 가능 / 게시글 수정 가능</li>
                                <li style={{ marginBottom: '4px' }}><strong>정회원:</strong> 병리과 전문의 / 질환 카테고리 / 갤러리 / comment 가능 / 회원공간 이용 가능</li>
                                <li style={{ marginBottom: '4px' }}><strong>일반회원:</strong> 병리과 전공의 / 질환 카테고리 / 갤러리 / comment 가능 / 회원공간 이용 가능</li>
                                <li style={{ marginBottom: '4px' }}><strong>학생회원:</strong> 질환 카테고리 / 갤러리 / 회원공간 이용 가능 (소병연연구회 제외)</li>
                                <li><strong>기타회원:</strong> Bepath 소개 열람 가능</li>
                            </ul>
                        }
                        type="info"
                        showIcon
                        icon={<InfoCircleOutlined />}
                        closable
                    />
                </AdminInfoBox>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                }}>
                    <Input.Search
                        placeholder="ID, 이름, 이메일, 소속, 휴대폰 검색"
                        allowClear
                        onSearch={(value) => {
                            setSearchKeyword(value);
                            setCurrentPage(1); // 검색 시 1페이지로 이동
                        }}
                        onChange={(e) => setSearchKeyword(e.target.value)}
                        style={{ width: 320 }}
                    />

                    <div style={{ display: 'flex', gap: '8px' }}>
                        <Button onClick={clickHiddenBtn} style={{ border: 0, boxShadow: 'none', cursor: 'default' }}></Button>
                        <Button type="primary" onClick={() => setIsRegisterModalOpen(true)}>
                            + 신규 유저 등록
                        </Button>
                    </div>
                </div>

                <StyledTable
                    size="small"
                    rowKey={'userId'}
                    columns={columns}
                    dataSource={activeUsers}
                    scroll={{ x: 'max-content' }}
                    // rowClassName={(record) =>
                    //     record.isWithdrawnYN ? 'withdrawn-user-row' : ''
                    // }
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '15', '20', '25'],
                        showTotal: (total) => `Total ${total}`,
                        onChange: (page, size) => {
                            setCurrentPage(page);
                            setPageSize(size);
                        },
                    }}
                />
            </ContentCard>

            <UserEditModal
                open={isModalOpen}
                user={selectedUser}
                onClose={handleModalClose}
                onSubmit={handleModalSubmit}
                onPasswordChange={handlePasswordChange}
                onDelete={handleDeleteUser}
            />

            <UserRegisterModal
                open={isRegisterModalOpen}
                onClose={() => setIsRegisterModalOpen(false)}
                onSuccess={refetch}
            />

            <UserLogModal
                open={isLogModalOpen}
                user={logTargetUser}
                onClose={() => setIsLogModalOpen(false)}
            />
        </>
    );
};

const AdminInfoBox = styled.div`
    margin-bottom: 24px;

    .ant-alert-info {
        background-color: #f0f7ff;
        border: 1px solid #adc6ff;
        border-radius: 8px;
    }

    .ant-alert-message {
        font-weight: bold;
        color: #1d39c4;
    }

    .ant-alert-description {
        color: #262626;
        font-size: 14px;
    }
`;

const StyledTable = styled(Table<UserData>)`
    .withdrawn-user-row > td {
        background-color: #f5f5f5 !important;
        color: #a0a0a0 !important;
    }

    .withdrawn-user-row:hover > td {
        background-color: #eaeaea !important;
    }
`;

export default UserManagePage;